import { assign, createMachine } from "xstate";
import { IZone } from "@nju/data-access-portal-dashboard";
import { IEsimQRCode } from "@nju/data-access-portal-sim-card";

export enum events {
  ERROR = "ERROR",
  SET_DATA = "SET_DATA",
  SET_ZONES = "SET_ZONES",
  CREATE_ORDER = "CREATE_ORDER",
  CREATE_ORDER_SUCCESS = "CREATE_ORDER_SUCCESS",
  CREATE_ORDER_ERROR = "CREATE_ORDER_ERROR",
  NO_ACTIVE_PRODUCTS = "NO_ACTIVE_PRODUCTS",
}

type IEvent =
  | { type: events.ERROR }
  | {
      type: events.SET_DATA;
      subState: IEsimQRCode["subState"] | undefined;
    }
  | {
      type: events.SET_ZONES;
      zones: IZone[];
    }
  | { type: events.CREATE_ORDER }
  | { type: events.CREATE_ORDER_SUCCESS }
  | { type: events.CREATE_ORDER_ERROR }
  | { type: events.NO_ACTIVE_PRODUCTS };

interface IContext {
  subState: IEsimQRCode["subState"] | undefined;
  refreshInterval: number;
  zones: IZone[] | undefined;
}

export const myAccountPageMachine = createMachine(
  {
    schema: {
      events: {} as IEvent,
      context: {} as IContext,
    },
    initial: "loading",
    states: {
      loading: {
        initial: "getInitialData",
        states: {
          getInitialData: {
            invoke: {
              src: "getInitialData",
            },
            on: {
              [events.SET_DATA]: {
                actions: "setData",
                target: "fetchZones",
              },
              [events.ERROR]: {
                target: "#error",
              },
              [events.CREATE_ORDER]: {
                target: "#createOrder",
              },
            },
          },
          fetchZones: {
            invoke: {
              src: "fetchZones",
            },
            on: {
              [events.SET_ZONES]: {
                actions: "setZones",
                target: "#idle",
              },
              [events.ERROR]: {
                target: "#error",
              },
              [events.CREATE_ORDER]: {
                target: "#createOrder",
              },
            },
          },
        },
      },
      createOrder: {
        id: "createOrder",
        invoke: {
          src: "createOrder",
        },
        on: {
          [events.CREATE_ORDER_SUCCESS]: {
            actions: "handleCreateOrderSuccess",
            target: "idle",
          },
          [events.CREATE_ORDER_ERROR]: {
            target: "error",
          },
          [events.NO_ACTIVE_PRODUCTS]: {
            actions: "handleNoActiveProducts",
            target: "idle",
          },
        },
      },
      idle: {
        id: "idle",
        on: {
          [events.CREATE_ORDER]: {
            target: "createOrder",
          },
        },
        after: [
          {
            delay: (context) => {
              return context.refreshInterval || Number.POSITIVE_INFINITY;
            },
            target: "refresh",
          },
        ],
      },
      refresh: {
        invoke: {
          src: "getInitialData",
        },
        on: {
          [events.SET_DATA]: {
            actions: "setData",
            target: "idle",
          },
          [events.ERROR]: {
            target: "error",
          },
          [events.CREATE_ORDER]: {
            target: "createOrder",
          },
        },
      },
      error: {
        id: "error",
        type: "final",
      },
    },
  },
  {
    actions: {
      setData: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_DATA") {
          return {
            subState: event.subState,
          };
        }

        return context;
      }),
      setZones: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_ZONES") {
          return {
            zones: event.zones,
          };
        }

        return context;
      }),
    },
  },
);
